import { FC, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MenuItemType, useMenus } from '@/core/api/query/menus.query';
import { useAuthStore } from '@/core/stores/auth.store';
import { IconPlus } from '@tabler/icons-react';
import { openForm } from '@/shared/utils/misc/formNavigation';
import ChildItems from './child-items';

interface SidebarProps {
    toggle?: () => void;
}

interface MenuItemProps {
    item: MenuItemType;
    path: string;
    toggle?: () => void;
    activeMenu: number;
    setActiveMenu: (menu: number) => void;
}

interface MenuAddButtonProps {
    canAdd: boolean;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const MenuAddButton: FC<MenuAddButtonProps> = ({ canAdd, onClick }) => {
    if (!canAdd) {
        return null;
    }

    return (
        <button
            type="button"
            onClick={onClick}
            className="flex items-center justify-center rounded-md p-1"
            style={{
                color: 'var(--app-text-muted)',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--app-surface-muted)';
                e.currentTarget.style.color = 'var(--app-text)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--app-text-muted)';
            }}
        >
            <IconPlus className="w-4 h-4" />
        </button>
    );
};

const MenuItem: FC<MenuItemProps> = ({
    item,
    path,
    toggle,
    activeMenu,
    setActiveMenu,
}) => {
    const navigate = useNavigate();
    const { authState } = useAuthStore();
    const user = authState.user;
    const permissions = user?.permissions || [];
    const canAdd = !!permissions.find((permission) => permission.docType === item?.docType)?.add;
    const [activeSubMenu, setActiveSubMenu] = useState<number>(0);

    const handleMenuClick = () => {
        setTimeout(() => {
            if (toggle) toggle();
        }, 2000);
    };

    const handleAddClick = () => {
        if (item.docType) {
            openForm(navigate, {
                docType: item.docType,
                action: 'add',
                displayMode: 'modal',
            });
        }
    };

    const handleMenuToggle = () => {
        setActiveMenu(activeMenu === item.id ? 0 : item.id);
    };

    return (
        <li key={item.id} className="relative">
            {item.subMenu?.length > 0 ? (
                <div className="mb-0.5">
                    <button
                        onClick={handleMenuToggle}
                        className="sidebar-nav-button"
                        data-active={activeMenu === item.id ? 'true' : undefined}
                    >
                        <div className="flex items-center gap-2">
                            {item?.icon ? <></> : null}
                            <span className="text-sm font-medium truncate">{item.label}</span>
                            <MenuAddButton
                                canAdd={canAdd}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleAddClick();
                                }}
                            />
                        </div>

                        <svg
                            className={`w-4 h-4 transition-transform duration-200 ${activeMenu === item.id ? 'rotate-180' : ''
                                }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>
                    <div
                        className={`overflow-hidden transition-all duration-150 ${activeMenu === item.id ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                            }`}
                    >
                        <ul
                            className="mt-1 space-y-0.5 px-3 border-l"
                            style={{ borderColor: 'var(--app-border)' }}
                        >
                            {item.subMenu.map((subItem) => (
                                <MenuItem
                                    key={subItem.id}
                                    item={subItem}
                                    path={path}
                                    toggle={toggle}
                                    activeMenu={activeSubMenu}
                                    setActiveMenu={setActiveSubMenu}
                                />
                            ))}
                        </ul>
                    </div>
                </div>
            ) : (
                <div className="rounded-lg px-3 py-1.5 transition-colors duration-150">
                    <div className="flex items-center gap-2">
                        <Link
                            to={item.url ?? '#'}
                            onClick={handleMenuClick}
                            className="sidebar-nav-link"
                            data-active={path === item.url ? 'true' : undefined}
                        >
                            {item?.icon ? <></> : null}
                            <span className="truncate">{item.label}</span>
                        </Link>

                        <MenuAddButton
                            canAdd={canAdd}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleAddClick();
                            }}
                        />
                    </div>

                    {item.listChild && item.docType && (
                        <div className="mt-2">
                            <ChildItems item={item} />
                        </div>
                    )}
                </div>
            )}
        </li>
    );
};

const TSK_ROUTE = '/master/TSK';

const MY_TASKS_MENU: MenuItemType = {
    id: -1,
    icon: null,
    label: 'My Tasks',
    url: TSK_ROUTE,
    isReport: false,
    subMenu: [
        { id: -7, icon: null, label: 'Dashboard', url: `${TSK_ROUTE}?view=sprint-dashboard`, isReport: false, subMenu: [] },
        { id: -2, icon: null, label: 'Sprint', url: `${TSK_ROUTE}?view=sprint`, isReport: false, subMenu: [] },
        { id: -3, icon: null, label: 'Backlog', url: `${TSK_ROUTE}?view=backlog`, isReport: false, subMenu: [] },
        { id: -4, icon: null, label: 'My Work', url: `${TSK_ROUTE}?view=mywork`, isReport: false, subMenu: [] },
        { id: -5, icon: null, label: 'Capacity', url: `${TSK_ROUTE}?view=capacity`, isReport: false, subMenu: [] },
    ]
};

const STARS_MENU: MenuItemType = {
    id: -6,
    icon: null,
    label: 'Stars',
    url: `${TSK_ROUTE}?view=stars`,
    isReport: false,
    subMenu: []
};
const Sidebar: FC<SidebarProps> = ({ toggle }) => {
    const [menu, setMenu] = useState<MenuItemType[]>([]);
    const [activeMenu, setActiveMenu] = useState<number>(0);
    const user = useAuthStore((state) => state.authState.user);
    const { data: menuData } = useMenus(user);
    const path = globalThis.location?.pathname ?? '';

    useEffect(() => {
        if (menuData) {
            setMenu(menuData);
            // Filter out TSK from API menu — it is hardcoded as a pinned item below
            setMenu(menuData.filter(item =>
                item.docType !== 'TSK' &&
                item.url !== TSK_ROUTE &&
                !item.subMenu?.some(sub => sub.docType === 'TSK' || sub.url === TSK_ROUTE)
            ));
        }
    }, [menuData]);

    return (
        <div className="flex min-h-[calc(100svh-6rem)] flex-col overflow-y-auto scrollbar-hide relative px-2 py-1">
            <ul className="space-y-0.5">

        {/* <li className="relative">
                    <div className="rounded-lg px-3 py-1.5 transition-colors duration-150">
                        <Link
                            to={TSK_ROUTE}
                            onClick={() => { if (toggle) setTimeout(toggle, 200); }}
                            className="sidebar-nav-link"
                            data-active={isOnTSK ? 'true' : undefined}
                        >
                            <IconLayoutKanban size={15} />
                            <span className="text-sm font-medium truncate">Task Master</span>
                        </Link>
                    </div>
                </li> */}


                <MenuItem
                    item={MY_TASKS_MENU}
                    path={path}
                    toggle={toggle}
                    activeMenu={activeMenu}
                    setActiveMenu={setActiveMenu}
                />
                
                <MenuItem
                    item={STARS_MENU}
                    path={path}
                    toggle={toggle}
                    activeMenu={activeMenu}
                    setActiveMenu={setActiveMenu}
                />

                {menu.map((item) => (
                    <MenuItem
                        key={item.id}
                        item={item}
                        path={path}
                        toggle={toggle}
                        activeMenu={activeMenu}
                        setActiveMenu={setActiveMenu}
                    />
                ))}
            </ul>
        </div>
    );
};

export default Sidebar;
